// IMAGE-SEO DOOR (Founder law 14/10/26, memory experts-image-seo-names-law): EVERY public image carries a keyword filename +
// alt + title/caption + ImageObject. This file is the ONE place those are made. Pure + client-safe (no fs / no db): components,
// server pages, sitemaps and JSON-LD all call it. DB/tag lookups live in lib/imageSeoServer.ts (server only).
//
// URL SHAPE:  /pic/<size>/<base64url(source path incl. ?v=)>/<keyword-slug>.webp
//   - the file NAME Google/Bing read is the Hebrew keyword slug (UTF-8, percent-encoded in the attribute — same Hebrew-slug
//     convention as /images/<קטגוריה>/<שם>-<id>.jpg, categories and articles; also matches the Hebrew queries people type);
//   - the source rides in the directory part, so the route is stateless (no DB, no registry) and the cache key is the source+size;
//   - served by app/pic/[size]/[key]/[name]/route.ts = the SAME thumb pipeline as /api/img/thumb (which stays, so no old URL breaks;
//     /api/* is robots-disallowed, /pic is crawlable — that is the whole point).
import type { ThumbSize } from '@/lib/imgUrl';

export type ImageKind = 'pro' | 'gallery' | 'article' | 'category' | 'review';
export interface SeoCtx {
  category?: string | null;   // Hebrew name or hyphenated slug ("ציפויים-לרכב" is fine)
  service?: string | null;    // the specific service, e.g. "ציפוי קרמי"
  city?: string | null;
  proName?: string | null;
  tags?: (string | null | undefined)[] | null;   // Hebrew gallery tag labels (he_gallery_image_tags → imageSeoServer.tagLabels)
  title?: string | null;      // article / page title
  alt?: string | null;        // an existing human-written alt worth keeping (article editor)
  n?: number | null;          // 1-based position in a gallery → keeps alt unique per image
}
export interface SeoImageInput { src: string; kind: ImageKind; ctx?: SeoCtx; size?: ThumbSize }
export interface SeoImageResult { url: string; alt: string; title: string; caption: string; slug: string; sourceUrl: string; kind: ImageKind }

const BASE = 'https://experts.co.il';
const NIQQUD = /[֑-ׇ]/g;
const clean = (s: unknown) => String(s ?? '').replace(NIQQUD, '').replace(/["'`׳״]/g, '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
const uniq = (a: string[]) => { const seen = new Set<string>(); return a.filter(x => { const k = x.toLowerCase(); if (!x || seen.has(k)) return false; seen.add(k); return true; }); };
const cut = (s: string, n: number) => s.length <= n ? s : s.slice(0, n).replace(/\s+\S*$/, '').trim();

/** Short + descriptive (Google Images 2026 guidance: a few words, not a keyword chain): Hebrew + Latin + digits, ≤ 4 words, ≤ 40 chars. */
export function slugify(parts: (string | null | undefined)[], maxLen = 40, maxWords = 4): string {
  const words = uniq(parts.map(clean).filter(Boolean).join(' ').replace(/[^֐-׿a-zA-Z0-9\s]/g, ' ').split(/\s+/).filter(Boolean));
  let out = '';
  for (const w of words.slice(0, maxWords)) { if ((out + '-' + w).length > maxLen) break; out = out ? out + '-' + w : w; }
  return out;
}

const b64 = (src: string) => typeof window === 'undefined' ? Buffer.from(src, 'utf8').toString('base64url')
  : btoa(unescape(encodeURIComponent(src))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

/** The slug a source path already tells us with NO context: /images/<קטגוריה>/<שם>-<id>.jpg → "קטגוריה-שם-id"; article images likewise. */
export function slugFromSrc(src: string): string {
  const p = src.split('?')[0]; let dec = p; try { dec = decodeURIComponent(p); } catch { /* keep */ }
  const m = dec.match(/^\/images\/([^/]+)\/([^/]+?)(?:\.[a-z0-9]+)?$/i);
  if (m) return slugify([m[1], m[2]]);
  return '';
}

/** /pic/<size>/<b64>/<slug>.webp — percent-encoded per segment (a raw >0xFF char in a header-bound URL crashed prod once: lib/imgUrl encodeImagePath). */
export function seoThumbUrl(src: string, size: ThumbSize, slug: string): string {
  return `/pic/${size}/${b64(src)}/${encodeURIComponent(slug)}.webp`;
}

/** HE preposition ב + city ("בתל אביב-יפו"). */
const inCity = (c?: string | null) => (c = clean(c)) ? `ב${c}` : '';
const joinTags = (t: string[]) => t.length <= 1 ? t.join('') : t.length === 2 ? `${t[0]} ו${t[1]}` : `${t.slice(0, -1).join(', ')} ו${t[t.length - 1]}`;
const isGenericTag = (t: string) => /^(אחר|אנשים|other)$/i.test(t);

function build(kind: ImageKind, c: SeoCtx): { alt: string; title: string; caption: string; slug: string[] } {
  const cat = clean(c.category), svc = clean(c.service), city = clean(c.city), pro = clean(c.proName), ttl = clean(c.title);
  const tags = uniq((c.tags || []).map(clean).filter(t => t && !isGenericTag(t))).slice(0, 3);
  const what = svc || cat;
  const tail = [what, inCity(city)].filter(Boolean).join(' ');   // "ציפוי קרמי בתל אביב"
  const n = c.n && c.n > 0 ? ` (${c.n})` : '';
  let alt = '', title = '', caption = '', slug: string[] = [];
  if (kind === 'pro') {
    alt = [pro, tail].filter(Boolean).join(' – ') || ttl || 'מומחה';
    title = [pro, what].filter(Boolean).join(' – ') || alt;
    caption = [pro && `${pro}${what ? `, ${what}` : ''}`, inCity(city)].filter(Boolean).join(' ') + (pro ? '' : '');
    slug = [pro, what];
  } else if (kind === 'gallery') {
    // FACTUAL: the tags are what the photo shows; the pro / service are its context (whose work, which trade). The city is NOT claimed
    // (a photo of a car does not show Tel Aviv) — it stays on the page, not in the image text.
    const tg = joinTags(tags);
    const ctxLine = `מעבודות ${pro || 'בעל מקצוע'}${what ? `, ${what}` : ''}`;
    alt = tg ? `${tg} – ${ctxLine}${n}` : `עבודה ${ctxLine.replace(/^מעבודות /, 'של ')}${n}`;
    title = [tg || what, pro].filter(Boolean).join(' – ');
    caption = `${tg ? tg + ' – ' : ''}${ctxLine}`;
    slug = tags.length ? [...tags.slice(0, 2), c.n ? String(c.n) : ''] : [what, 'עבודה', c.n ? String(c.n) : ''];
  } else if (kind === 'article') {
    // stored FAQ alts end with a bare/hyphenated category after a question ("…? דלתות פנים") → drop that tail
    const fixed = clean(c.alt).replace(/([?!])\s*(?:[-–]\s*)?[^?!.:–-]{2,30}$/, "$1");
    // card alt: no year, no ": marketing tail", short
    const short = ttl.replace(/\s*\b20\d\d\b/g, "").split(/:|\s[—–]\s/)[0].replace(/\s+/g, " ").trim().slice(0, 60);
    const base = fixed || short;
    alt = base ? (c.alt ? base : `איור למאמר: ${base}`) : [cat, 'מאמר'].filter(Boolean).join(' – ');
    title = ttl || base || cat;
    caption = ttl ? `${ttl}${cat ? ` – ${cat}` : ''}` : alt;
    slug = [ttl || base, cat];
  } else if (kind === 'category') {
    alt = (ttl && what && ttl.includes(what) ? ttl : [what, ttl].filter(Boolean).join(' – ')) || cat;
    title = alt; caption = alt;
    slug = [ttl, what, city];
  } else {
    alt = `תמונה שצירף לקוח לביקורת על ${pro || 'בעל מקצוע'}${what ? `, ${what}` : ''}${n}`;
    title = `ביקורת לקוח – ${[pro, what].filter(Boolean).join(', ')}`.replace(/ – $/, '');
    caption = alt;
    slug = ['ביקורת', what, c.n ? String(c.n) : ''];
  }
  return { alt: cut(alt.replace(/\s+/g, ' ').trim(), 125), title: cut(title.replace(/\s+/g, ' ').trim(), 100), caption: cut(caption.replace(/\s+/g, ' ').trim(), 250), slug };
}

/**
 * THE door. Non-/api-thumbable sources (external avatars, data:, generated) keep their own URL but still get alt/title/caption.
 * `size` defaults: pro/review → 288 square, gallery/article/category → w960 (the ImageObject/sitemap representative size).
 */
export function seoImage(input: SeoImageInput): SeoImageResult {
  const { src, kind } = input; const c = input.ctx || {};
  const b = build(kind, c);
  const slug = slugify(b.slug) || slugFromSrc(src) || slugify([kind === 'pro' ? 'מומחה' : 'תמונה']);
  const size: ThumbSize = input.size ?? ((kind === 'pro' || kind === 'review') ? 288 : 'w960');
  const thumbable = src.startsWith('/images/') || src.startsWith('/api/img/') || src.startsWith('/img/pricing/') || src.startsWith('/img/topics/');
  return { url: thumbable ? seoThumbUrl(src, size, slug) : src, alt: b.alt, title: b.title, caption: b.caption, slug, sourceUrl: src, kind };
}

const SQ = [48, 72, 96, 144, 192, 288, 384, 576, 864] as const;
const WIDE = [320, 480, 640, 960, 1280] as const;
const pickSq = (px: number) => SQ.find(s => s >= px) ?? 864;
type NoSrc = Omit<SeoImageInput, 'src' | 'size'>;

/** Square avatar: <img {...seoAvatar(src, 54, { kind:'pro', ctx })} /> → src/srcSet (1x/2x/3x) + alt + title, all keyword-named. */
export function seoAvatar(src: string | null | undefined, cssPx: number, input: NoSrc): { src: string; srcSet?: string; alt: string; title: string } {
  if (!src) return { src: '', alt: seoImage({ src: '', ...input }).alt, title: '' };
  const r = seoImage({ src, ...input, size: pickSq(cssPx) as ThumbSize });
  if (r.url === src) return { src, alt: r.alt, title: r.title };
  return { src: r.url, srcSet: [1, 2, 3].map(d => `${seoThumbUrl(src, pickSq(cssPx * d) as ThumbSize, r.slug)} ${d}x`).join(', '), alt: r.alt, title: r.title };
}
/** Wide photo (no crop), width-descriptor srcSet. */
export function seoWide(src: string | null | undefined, sizes: string, input: NoSrc, fallbackW: 320 | 480 | 640 | 960 = 640): { src: string; srcSet?: string; sizes?: string; alt: string; title: string } {
  if (!src) return { src: '', alt: seoImage({ src: '', ...input }).alt, title: '' };
  const r = seoImage({ src, ...input, size: `w${fallbackW}` as ThumbSize });
  if (r.url === src) return { src, alt: r.alt, title: r.title };
  return { src: r.url, srcSet: WIDE.map(w => `${seoThumbUrl(src, `w${w}` as ThumbSize, r.slug)} ${w}w`).join(', '), sizes, alt: r.alt, title: r.title };
}

/** schema.org ImageObject for a seoImage() result — schemaLd.ts calls this (absolute URLs). */
export function imageObjectLd(r: SeoImageResult, extra: { width?: number; height?: number; representativeOfPage?: boolean } = {}): Record<string, unknown> {
  const abs = r.url.startsWith('http') ? r.url : BASE + r.url;
  return { '@type': 'ImageObject', contentUrl: abs, url: abs, name: r.title, caption: r.caption, description: r.alt, inLanguage: 'he', ...(extra.width ? { width: extra.width } : {}), ...(extra.height ? { height: extra.height } : {}), ...(extra.representativeOfPage ? { representativeOfPage: true } : {}) };
}

/** Per-page og:image / twitter:image (signals agent): ABSOLUTE crawlable keyword-named URL + alt + dimensions. Square 576 for pros, 1200-wide for wide kinds.
 *  Returns Next `metadata.openGraph.images` shape: openGraph: { images: [ogImage({...})] }. */
export function ogImage(input: SeoImageInput): { url: string; width: number; height?: number; alt: string; type: string } {
  const wide = input.kind !== 'pro' && input.kind !== 'review';
  const r = seoImage({ ...input, size: input.size ?? (wide ? 'w1280' : 576) });
  const url = r.url.startsWith('http') ? r.url : BASE + r.url;
  return wide ? { url, width: 1200, alt: r.alt, type: 'image/webp' } : { url, width: 576, height: 576, alt: r.alt, type: 'image/webp' };
}
